import React, { useCallback, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useAtomValue } from 'jotai/react'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'

import { imageSchema } from '@/constants/schema'
import usePostCreate from '@/hooks/services/usePostCreate'
import { tokenInfoAtom } from '@/stores/services'

import { AsyncButton } from '../common/Button'
import { Textarea } from '../common/Input'
import { Text } from '../common/Text'
import { ImageUploader } from '../common/Uploader'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '../ui/form'

const FORM_SETTING = {
  maxComment: 3000,
  maxFiles: 1,
  maxSize: 4 * 1024 * 1024 // 4MB
}

const PostForm: React.FC<{ close: () => void; replyId?: number }> = ({
  replyId,
  close
}) => {
  const { t } = useTranslation()
  const formSchema = z.object({
    msg: z.string().trim().min(1).max(FORM_SETTING.maxComment), // 字符串类型
    file: imageSchema(
      t('common.imageError', { type: 'jpg|jpeg|png|gif', size: '4MB' })
    ).optional() // 可选的图片类型
  })

  const [createParams, setCreateParams] = useState<FormData>()
  const tokenInfo = useAtomValue(tokenInfoAtom)

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      msg: ''
    }
  })

  const msg = form.watch('msg')

  const { isLoading } = usePostCreate(createParams, {
    onSuccess: () => {
      close()
      toast.success(t('coin.postSuccess'))
      setCreateParams(undefined)
      form.reset()
    }
  })

  const onSubmit = useCallback(
    (values: z.infer<typeof formSchema>) => {
      console.log('>>>>>> onSubmit: ', tokenInfo, values)
      if (!tokenInfo) return
      const formData = new FormData()
      formData.append('msg', values.msg)
      formData.append('tokenId', tokenInfo.id.toString())
      formData.append('replyId', (replyId || 0).toString())
      if (values.file) formData.append('file', values.file)

      setCreateParams(formData)
    },
    [replyId, tokenInfo]
  )

  return (
    <Form {...form}>
      <form
        role="post-form"
        className="space-y-6 xl:space-y-10"
        onSubmit={form.handleSubmit(onSubmit, console.log)}
      >
        <FormField
          control={form.control}
          name="msg"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Textarea
                  {...field}
                  placeholder={t('coin.reply.comment')}
                  maxLength={FORM_SETTING.maxComment}
                />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="file"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                <Text as="span" className="text-xs xl:text-xl">
                  {t('coin.reply.image')}
                </Text>
              </FormLabel>
              <FormControl>
                <ImageUploader
                  name="post-file"
                  onChange={(file) => field.onChange(file)}
                  maxSize={FORM_SETTING.maxSize}
                  maxFiles={FORM_SETTING.maxFiles}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <AsyncButton
          role="post-submit"
          type="submit"
          size="default"
          loading={isLoading}
          disabled={!msg || isLoading || !tokenInfo}
          className="w-full font-Orbitron"
        >
          {t('coin.postReplay')}
        </AsyncButton>
      </form>
    </Form>
  )
}

export default PostForm
